import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { startGoogleOAuth } from "../lib/auth";
import { toast } from "sonner";

export default function Login() {
  const { loginJwt, registerJwt } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login"); // "login" | "register"
  const [form, setForm] = useState({ email: "", password: "", name: "" });
  const [busy, setBusy] = useState(false);

  const handleGoogle = () => startGoogleOAuth("/oracle");

  const handleJwt = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "login") {
        await loginJwt(form.email, form.password);
        toast.success("Welcome back.");
      } else {
        await registerJwt(form.email, form.password, form.name);
        toast.success("Initiation complete.");
      }
      // Navigate immediately — ThresholdRitual (mounted globally) will
      // detect the fresh session and stage the welcome + push-enable
      // nudge. Keeping first-session onboarding centralized in ONE
      // component avoids modal collisions.
      navigate("/archive");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Authentication failed.");
    } finally {
      setBusy(false);
    }
  };

  let submitLabel = "Sign in";
  if (busy) submitLabel = "Crossing…";
  else if (mode === "register") submitLabel = "Create account";

  return (
    <div data-testid="login-page" className="max-w-md mx-auto px-6 py-24">
      <div className="label mb-6">Threshold</div>
      <h1 className="serif text-5xl text-foreground leading-tight">
        {mode === "login" ? "Re-enter the Archive." : "Become a contributor."}
      </h1>
      <p className="mt-4 text-muted-foreground leading-relaxed">
        {mode === "login" ? "Sign in to contribute correlations and converse with the Oracle." : "Create an account to submit and persist your reflections."}
      </p>

      <button
        onClick={handleGoogle}
        data-testid="google-login-btn"
        className="btn-ghost w-full mt-12 flex items-center justify-center gap-3"
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M21.35 11.1H12v3.2h5.35c-.55 2.6-2.7 4.5-5.35 4.5a6.3 6.3 0 1 1 0-12.6c1.55 0 3 .55 4.1 1.55l2.3-2.3A9.5 9.5 0 0 0 12 2a10 10 0 1 0 9.35 13.4c.45-1.3.65-2.7.65-4.3z"/></svg>
        Continue with Google
      </button>

      <div className="mt-10 mb-10 flex items-center gap-4">
        <div className="flex-1 hairline-t" />
        <span className="label">or</span>
        <div className="flex-1 hairline-t" />
      </div>

      <form onSubmit={handleJwt} className="space-y-8">
        {mode === "register" && (
          <div>
            <div className="label mb-3">Name</div>
            <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                   className="input-line w-full" data-testid="login-name" />
          </div>
        )}
        <div>
          <div className="label mb-3">Email</div>
          <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                 className="input-line w-full" data-testid="login-email" />
        </div>
        <div>
          <div className="label mb-3">Password</div>
          <input required type="password" minLength={mode === "register" ? 10 : 6} value={form.password}
                 onChange={(e) => setForm({ ...form, password: e.target.value })}
                 className="input-line w-full" data-testid="login-password" />
          {mode === "register" && (
            <p className="mt-2 mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
              At least 10 characters
            </p>
          )}
          {mode === "login" && (
            <div className="mt-3 text-right">
              <Link
                to="/forgot-password"
                data-testid="forgot-password-link"
                className="mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground hover:text-[hsl(var(--primary))] transition-colors"
              >
                Forgot password?
              </Link>
            </div>
          )}
        </div>
        <button type="submit" disabled={busy} data-testid="login-submit"
                className="btn-gold w-full disabled:opacity-40">
          {submitLabel}
        </button>
      </form>

      <div className="mt-10 text-center">
        <button onClick={() => setMode(mode === "login" ? "register" : "login")}
                data-testid="login-toggle-mode"
                className="mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground link-underline">
          {mode === "login" ? "New here? Create account →" : "Already initiated? Sign in →"}
        </button>
      </div>
    </div>
  );
}
